// src/phase4/systems/TowerSystem.ts
// Tower logic management system

import Phaser from 'phaser';
import { TowerInstance, TowerType, TowerLevelConfig } from '../types/tower';
import type { EnemyInstance } from '../types/enemy';
import { getTowerLevelConfig } from '../data/tower-configs';

export interface ProjectileData {
  id: string;
  towerId: string;
  targetId: string;
  x: number;
  y: number;
  damage: number;
  speed: number;
  hasSlowEffect: boolean;
  hasPierceEffect: boolean;
  isCritical: boolean;
  towerType: TowerType;
}

export interface TowerFireCallback {
  (projectile: ProjectileData): void;
}

export interface TowerUpgradeCallback {
  (towerId: string, newLevel: number, cost: number): void;
}

export class TowerSystem {
  private towers: Map<string, TowerInstance> = new Map();

  // Callbacks
  private onFire?: TowerFireCallback;
  private onUpgrade?: TowerUpgradeCallback;

  constructor(_scene: Phaser.Scene) {
    // Reserved for future use: time-based abilities, projectile spawning, etc.
  }

  // Callback setters
  setFireCallback(callback: TowerFireCallback): void {
    this.onFire = callback;
  }

  setUpgradeCallback(callback: TowerUpgradeCallback): void {
    this.onUpgrade = callback;
  }

  // Tower creation
  createTower(
    id: string,
    type: TowerType,
    x: number,
    y: number,
    pathName?: string
  ): TowerInstance {
    // Verify level config exists
    void getTowerLevelConfig(type, pathName || this.getDefaultPathName(type), 1);

    const tower: TowerInstance = {
      id,
      type,
      currentLevel: 1,
      x,
      y,
      targetId: null,
      lastFireTime: 0,
    };

    this.towers.set(id, tower);
    console.log(`[TowerSystem] Created tower ${id} (${type}) at (${x}, ${y})`);

    return tower;
  }

  private getDefaultPathName(type: TowerType): string {
    switch (type) {
      case TowerType.CPU_MAIN:
        return 'CPU';
      case TowerType.LANGUAGE_SIDE:
        return 'C';
      case TowerType.NETWORK_SIDE:
        return 'Apache';
      default:
        return 'CPU';
    }
  }

  // Main update loop - towers find targets and fire
  update(
    _delta: number,
    enemies: EnemyInstance[],
    currentTime: number
  ): void {
    for (const tower of this.towers.values()) {
      const levelConfig = this.getLevelConfig(tower);
      if (!levelConfig) continue;

      // Find target if none or current target is invalid
      if (!tower.targetId || !this.isValidTarget(tower.targetId, enemies, tower)) {
        tower.targetId = this.findNearestTarget(tower, enemies);
      }

      // Fire at target
      if (tower.targetId && currentTime - tower.lastFireTime >= levelConfig.fireRate) {
        this.fireTower(tower, levelConfig);
        tower.lastFireTime = currentTime;
      }
    }
  }

  private getLevelConfig(tower: TowerInstance): TowerLevelConfig | undefined {
    const pathName = this.getPathNameForTower(tower.type);
    return getTowerLevelConfig(tower.type, pathName, tower.currentLevel);
  }

  private getPathNameForTower(type: TowerType): string {
    // For now, assume single path per type for simplicity
    // In full implementation, store pathName in tower instance
    switch (type) {
      case TowerType.CPU_MAIN:
        return 'CPU';
      case TowerType.LANGUAGE_SIDE:
        return 'C';
      case TowerType.NETWORK_SIDE:
        return 'Apache';
      default:
        return 'CPU';
    }
  }

  private isValidTarget(
    targetId: string,
    enemies: EnemyInstance[],
    tower: TowerInstance
  ): boolean {
    const levelConfig = this.getLevelConfig(tower);
    if (!levelConfig) return false;

    const target = enemies.find((e) => e.id === targetId);
    if (!target || target.isDead) return false;

    // Check range
    const distance = Phaser.Math.Distance.Between(tower.x, tower.y, target.x, target.y);
    if (distance > levelConfig.range) return false;

    // Check stealth (can only target stealthed enemies if already targeting them)
    if (target.isStealthed && tower.targetId !== targetId) return false;

    return true;
  }

  private findNearestTarget(
    tower: TowerInstance,
    enemies: EnemyInstance[]
  ): string | null {
    const levelConfig = this.getLevelConfig(tower);
    if (!levelConfig) return null;

    let nearestTarget: string | null = null;
    let nearestDistance = levelConfig.range;

    for (const enemy of enemies) {
      if (enemy.isDead) continue;
      if (enemy.isStealthed) continue; // Can't auto-target stealthed

      const distance = Phaser.Math.Distance.Between(tower.x, tower.y, enemy.x, enemy.y);
      if (distance <= levelConfig.range && distance < nearestDistance) {
        nearestDistance = distance;
        nearestTarget = enemy.id;
      }
    }

    return nearestTarget;
  }

  private fireTower(tower: TowerInstance, levelConfig: TowerLevelConfig): void {
    // Calculate damage with critical chance
    let damage = levelConfig.dps * (levelConfig.fireRate / 1000);
    let isCritical = false;

    const critEffect = levelConfig.specialEffects?.find((e) => e.type === 'critical');
    if (critEffect && Math.random() < critEffect.value) {
      damage *= 2;
      isCritical = true;
    }

    // Check for special effects
    const hasSlowEffect = levelConfig.specialEffects?.some((e) => e.type === 'slow');
    const hasPierceEffect = levelConfig.specialEffects?.some((e) => e.type === 'pierce');

    const projectile: ProjectileData = {
      id: `proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      towerId: tower.id,
      targetId: tower.targetId!,
      x: tower.x,
      y: tower.y,
      damage,
      speed: 8,
      hasSlowEffect: !!hasSlowEffect,
      hasPierceEffect: !!hasPierceEffect,
      isCritical,
      towerType: tower.type,
    };

    this.onFire?.(projectile);
  }

  // Upgrade tower
  upgradeTower(towerId: string): boolean {
    const tower = this.towers.get(towerId);
    if (!tower) return false;

    const pathName = this.getPathNameForTower(tower.type);
    const currentConfig = getTowerLevelConfig(tower.type, pathName, tower.currentLevel);
    const nextConfig = getTowerLevelConfig(tower.type, pathName, tower.currentLevel + 1);

    if (!currentConfig || !nextConfig) return false;

    tower.currentLevel++;
    this.onUpgrade?.(towerId, tower.currentLevel, nextConfig.upgradeCost);

    console.log(`[TowerSystem] Tower ${towerId} upgraded to level ${tower.currentLevel}`);

    return true;
  }

  // Get upgrade info
  getUpgradeInfo(towerId: string): { canUpgrade: boolean; cost: number; nextLevel: number } | null {
    const tower = this.towers.get(towerId);
    if (!tower) return null;

    const pathName = this.getPathNameForTower(tower.type);
    const nextConfig = getTowerLevelConfig(tower.type, pathName, tower.currentLevel + 1);

    if (!nextConfig) {
      return { canUpgrade: false, cost: 0, nextLevel: tower.currentLevel };
    }

    return {
      canUpgrade: true,
      cost: nextConfig.upgradeCost,
      nextLevel: tower.currentLevel + 1,
    };
  }

  // Sell tower - returns the refund amount (50% of total invested)
  sellTower(towerId: string): number {
    const tower = this.towers.get(towerId);
    if (!tower) return 0;

    // Calculate total invested (sum of all upgrade costs up to current level)
    const pathName = this.getPathNameForTower(tower.type);
    let totalInvested = 0;
    for (let level = 1; level <= tower.currentLevel; level++) {
      const config = getTowerLevelConfig(tower.type, pathName, level);
      if (config) {
        totalInvested += config.upgradeCost;
      }
    }

    // Refund 50%
    const refund = Math.floor(totalInvested * 0.5);

    // Remove tower
    this.towers.delete(towerId);

    console.log(`[TowerSystem] Tower ${towerId} sold for ${refund} FLOP (invested: ${totalInvested})`);
    return refund;
  }

  // Get sell value (50% refund)
  getSellValue(towerId: string): number {
    const tower = this.towers.get(towerId);
    if (!tower) return 0;

    const pathName = this.getPathNameForTower(tower.type);
    let totalInvested = 0;
    for (let level = 1; level <= tower.currentLevel; level++) {
      const config = getTowerLevelConfig(tower.type, pathName, level);
      if (config) {
        totalInvested += config.upgradeCost;
      }
    }

    return Math.floor(totalInvested * 0.5);
  }

  // Get tower stats
  getTowerStats(towerId: string): TowerLevelConfig | null {
    const tower = this.towers.get(towerId);
    if (!tower) return null;

    return this.getLevelConfig(tower) || null;
  }

  // Getters
  getTower(id: string): TowerInstance | undefined {
    return this.towers.get(id);
  }

  getAllTowers(): TowerInstance[] {
    return Array.from(this.towers.values());
  }

  getTowersByType(type: TowerType): TowerInstance[] {
    return Array.from(this.towers.values()).filter((t) => t.type === type);
  }

  // Reset
  reset(): void {
    this.towers.clear();
  }
}
